import { useCallback, useRef, useState } from "react";
import { ModalMolecule } from "app/components/molecules/modal";
import { Button, OutlineButton } from "app/components/atoms/button";

/**
 * Pengganti `window.confirm` berbasis modal aplikasi.
 *
 * Dialog native memblokir seluruh event browser: otomatisasi (Playwright,
 * Claude in Chrome) membeku begitu tombol yang memanggilnya diklik, dan
 * dialognya tidak bisa diberi data-testid. Hook ini mengembalikan fungsi
 * `confirm(message)` yang resolve `true`/`false` — pemakaian di pemanggil
 * hanya berubah dari `window.confirm(x)` menjadi `await confirm(x)`.
 *
 *   const { confirm, ConfirmDialog } = useConfirm();
 *   ...
 *   if (!(await confirm("Yakin?"))) return;
 *   ...
 *   return (<>{ConfirmDialog}...</>);
 */
export function useConfirm({ title = "Konfirmasi", testId = "confirm-dialog" } = {}) {
  const [state, setState] = useState(null); // { message }
  const resolver = useRef(null);

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ message });
    });
  }, []);

  const settle = (value) => {
    setState(null);
    const r = resolver.current;
    resolver.current = null;
    if (r) r(value);
  };

  const ConfirmDialog = (
    <ModalMolecule show={!!state} title={title} onClose={() => settle(false)}>
      <div>
        <p data-testid={`${testId}-message`}>{state?.message}</p>
        <div className="mt-6 flex justify-end gap-2">
          <OutlineButton testId={`${testId}-batal-button`} type="button" onClick={() => settle(false)}>
            Batal
          </OutlineButton>
          <Button testId={`${testId}-ya-button`} type="button" onClick={() => settle(true)}>
            Ya
          </Button>
        </div>
      </div>
    </ModalMolecule>
  );

  return { confirm, ConfirmDialog };
}
